import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useTitle } from "../../hooks/useTitle";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TemplatePreview } from "../../components/TemplatePreview";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    padding: theme.spacing(2),
  },
  gridContainer: {
    paddingTop: theme.spacing(3),
  },
  card: {
    display: "flex",
    flexDirection: "column",
    height: "250px",
  },
  cardContent: {
    flex: 1,
    overflow: "hidden",
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    padding: 0,
  },
  cardTitle: {
    padding: theme.spacing(2, 2, 0, 2),
    fontWeight: 'bold',
  },
  cardActions: {
    justifyContent: "flex-end",
  },
  addCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "250px",
    cursor: "pointer",
    textAlign: 'center',
    border: `2px dashed ${theme.palette.grey[400]}`,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  skeletonContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

const PrescriptionTemplates = () => {
  useTitle("Plantillas para Recetas");
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    setLoading(true); 
    
    const fetchTemplates = async () => {
      try {
        const { data } = await api.get("/prescriptionTemplates");
        setTemplates(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false); 
      }
    };
    fetchTemplates();
  }, []);

  const handleCreate = () => {
    history.push("/PrescriptionTemplates/new");
  };

  const handleEdit = (template) => {
    history.push(`/PrescriptionTemplates/${template.id}`);
  };

  const handleDelete = (template) => {
    setSelectedTemplate(template);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await api.delete(`/prescriptionTemplates/${selectedTemplate.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== selectedTemplate.id));
      toast.info("Plantilla eliminada correctamente");
    } catch (err) {
      toastError(err);
    } finally {
      setModalOpen(false);
      setSelectedTemplate(null);
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const { data: newTemplate } = await api.post(
        `/prescriptionTemplates/${template.id}/duplicate`
      );
      
      setTemplates((prev) => [...prev, newTemplate]);
      
      toast.success(`Plantilla "${template.name}" duplicada con éxito.`);

      history.push(`/PrescriptionTemplates/${newTemplate.id}`);

    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Plantillas de Recetas</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} elevation={0}>
        <Typography variant="body1" paragraph>
          Aquí puedes crear, editar y gestionar tus plantillas de recetas médicas.
          Las plantillas marcadas como "duplicables" pueden ser copiadas por otros
          doctores de tu clínica para crear sus propias versiones.
        </Typography>

        <Grid container spacing={3} className={classes.gridContainer}>
          {loading ? (
            <>
              {[1, 2, 3, 4, 5].map((n) => (
                <Grid item xs={12} sm={6} md={4} key={`skeleton-${n}`}>
                  <Card className={classes.card} variant="outlined">
                    <div className={classes.skeletonContent}>
                      {/* Skeleton para el título */}
                      <Skeleton variant="text" width="60%" style={{ margin: '16px 16px 8px' }} />
                      {/* Skeleton para el área de preview */}
                      <Skeleton variant="rect" style={{ flex: 1, margin: '0 16px' }} />
                      {/* Skeleton para los botones de acción */}
                      <Skeleton variant="text" width="40%" style={{ alignSelf: 'flex-end', margin: '16px' }}/>
                    </div>
                  </Card>
                </Grid>
              ))}
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <Card className={classes.addCard} onClick={handleCreate} variant="outlined">
                  <AddIcon fontSize="large" color="primary" />
                  <Typography variant="subtitle1">Crear Nueva Plantilla</Typography>
                </Card>
              </Grid>

              {templates.map((template) => {
                const isOwner = template.userId === user.id;

                return (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Card className={classes.card} variant="outlined">
                      <Typography className={classes.cardTitle}>{template.name}</Typography>
                      
                      <CardContent className={classes.cardContent}>
                        {template.content ? (
                          <TemplatePreview content={template.content} />
                        ) : (
                          <div style={{ padding: 20, textAlign: 'center' }}>
                            <Typography color="textSecondary">Plantilla vacía</Typography>
                          </div>
                        )}
                      </CardContent>

                      <CardActions className={classes.cardActions}>
                        <Tooltip title="Duplicar plantilla">
                          <span>
                            <IconButton
                              onClick={() => handleDuplicate(template)}
                              size="small"
                              disabled={!template.duplicable}
                            >
                              <FileCopyIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        
                        {isOwner && (
                          <>
                            <Tooltip title="Editar plantilla">
                              <IconButton onClick={() => handleEdit(template)} size="small">
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar plantilla">
                              <IconButton onClick={() => handleDelete(template)} size="small">
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </>
          )}
        </Grid>
      </Paper>

      <ConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirmar eliminación"
      >
        <p>
          ¿Seguro que deseas eliminar la plantilla "{selectedTemplate?.name}"?
        </p>
      </ConfirmationModal>
    </MainContainer>
  );
};

export default PrescriptionTemplates;